import { SHOOTER_GENDER_LABELS, type ShooterGender } from './shooting';

/** The leaderboard fields the results file needs. */
export interface ResultsRow {
  round2?: { team: string; shots: string[]; total: string; teamRank: number; overall: string } | null;
  rank: number;
  name?: string;
  bib?: string | null;
  gender?: ShooterGender | null;
  teamName?: string | null;
  shotsFired?: number;
  shotsPlanned?: number;
  totalDisplay: string;
  tens?: number;
  shots?: { shotNumber: number; pointsDisplay: string; counted?: boolean }[];
}

/** One CSV cell: quoted when needed, and spreadsheet formulas neutralised (a leading = + - @ gets a '). */
function cell(value: string | number | null | undefined): string {
  let text = value == null ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * Results as CSV (UTF-8 with BOM so Excel shows names correctly): Rank, Name, RFL, Gender, Shots, Total,
 * 10s, then Shot 1..N (dropped shots marked "(out)"). Unscored shooters have an empty rank and total.
 */
export function resultsCsv(rows: ResultsRow[], shotsPlanned: number, teams?: readonly { name: string; totalDisplay: string }[]): string {
  const shotCount = Math.max(shotsPlanned, ...rows.map((row) => Math.max(0, ...(row.shots ?? []).map((shot) => shot.shotNumber))));
  const hasRound2 = rows.some((row) => 'round2' in row);
  const round2Headers = hasRound2 ? ['Round 2 team', 'Round 2 shots', 'Round 2 total', 'Round 2 team rank', 'Overall'] : [];
  const header = ['Rank', 'Name', 'RFL', 'Gender', 'Team', 'Shots', 'Total', '10s', ...Array.from({ length: shotCount }, (_, i) => `Shot ${i + 1}`), ...round2Headers];
  const order = new Map(teams?.map((team, index) => [team.name, index]) ?? []);
  const orderedRows = teams ? [...rows].sort((a, b) =>
    (order.get(a.teamName ?? '') ?? Number.MAX_SAFE_INTEGER) - (order.get(b.teamName ?? '') ?? Number.MAX_SAFE_INTEGER)
    || (a.bib ?? '').localeCompare(b.bib ?? ''),
  ) : rows;
  const lines = orderedRows.map((row) => {
    const ranked = row.rank > 0;
    const byNumber = new Map((row.shots ?? []).map((shot) => [shot.shotNumber, shot]));
    return [
      ranked ? row.rank : '',
      row.name ?? '',
      row.bib ?? '',
      row.gender ? SHOOTER_GENDER_LABELS[row.gender] : '',
      row.teamName ?? '',
      `${row.shotsFired ?? 0}/${row.shotsPlanned ?? shotsPlanned}`,
      ranked ? row.totalDisplay : '',
      ranked ? (row.tens ?? 0) : '',
      ...Array.from({ length: shotCount }, (_, i) => {
        const shot = byNumber.get(i + 1);
        if (!shot) return '';
        return shot.counted === false ? `${shot.pointsDisplay} (out)` : shot.pointsDisplay;
      }),
      ...(hasRound2 ? [row.round2?.team ?? '', row.round2?.shots.join(' / ') ?? '', row.round2?.total ?? '', row.round2?.teamRank || '', row.round2?.overall ?? ''] : []),
    ]
      .map(cell)
      .join(',');
  });
  if (teams) {
    let offset = 0;
    for (const team of teams) {
      const count = orderedRows.filter((row) => row.teamName === team.name).length;
      if (count === 0) continue;
      lines.splice(offset + count, 0, ['', `${team.name} total`, '', '', team.name, '', team.totalDisplay, '', ...Array(shotCount + round2Headers.length).fill('')].map(cell).join(','));
      offset += count + 1;
    }
  }
  return `\uFEFF${[header.map(cell).join(','), ...lines].join('\r\n')}\r\n`;
}

/** File name like rifle-shooting-2026-results-women-2026-11-01.csv */
export function resultsFileName(slug: string, filter: 'all' | ShooterGender, now = new Date()): string {
  const group = filter === 'all' ? '' : `-${filter === 'male' ? 'men' : 'women'}`;
  const day = new Date(now.getTime() + 5.5 * 3600_000).toISOString().slice(0, 10);
  return `${slug}-results${group}-${day}.csv`;
}
